import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { exchangeGoogleCode, verifyTenant } from 'api/auth/index';
import { useAuth } from 'context/AuthContext';
import loadingGif from 'images/loader2.webp';
import { notifyError, notifySuccess } from 'utils/customToasts';
import { extractTenantName, getOrigin, getTenantId } from 'utils/helper';

const CallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, saveGoogleAuthData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasProcessed = useRef(false);

  // Removed getTenantIdFromAPI function - now using utility function directly

  const exchangeCode = async () => {
    // Prevent multiple executions using ref (persists across re-renders)
    if (hasProcessed.current) {
      setLoading(false);
      return navigate('/chat');
    }
    
    // Prevent multiple executions
    if (isProcessing) {
      return;
    }

    // Check if already logged in using AuthContext
    if (token && user) {
      setLoading(false);
      return navigate('/chat');
    }

    // Check if this callback has already been processed
    const processedCallback = sessionStorage.getItem("googleCallbackProcessed");
    if (processedCallback) {
      setLoading(false);
      return navigate('/chat');
    }

    // Mark as processing immediately to prevent race conditions
    hasProcessed.current = true;
    setIsProcessing(true);
    sessionStorage.setItem("googleCallbackProcessed", "true");
    
    try {
      const params = new URLSearchParams(location.search);

      const state = params.get('state');
      const code = params.get('code');

      if (!code || !state) {
        notifyError('Failed to login with Google');
        return navigate('/');
      }

      const payload = {
        code,
        state
      }

      const response = await exchangeGoogleCode(payload);

      if (!response?.status || response?.status_code !== 200) {
        notifyError(response?.message || 'Google login failed');
        return navigate('/');
      }

      const tenantId = await getTenantId();

      if (!tenantId || !response?.data?.token) {
        notifyError('Failed to login with Google');
        return navigate('/');
      }

      // Use AuthContext's saveGoogleAuthData to properly handle Google OAuth
      const authData = {
        token: response?.data?.token,
        tenantId: tenantId,
        user: response?.data?.user || response?.data
      };
      
      saveGoogleAuthData(authData);
      
      // Mark this callback as processed to prevent re-execution
      sessionStorage.setItem("googleCallbackProcessed", "true");

      setLoading(false);
      notifySuccess("Google login successful!");
      navigate('/chat');



    } catch (error) {
      notifyError(error?.message || 'Google login failed');
      // Mark as processed even on error to prevent retry with same callback
      sessionStorage.setItem("googleCallbackProcessed", "true");
      navigate('/');
    } finally {
      setIsProcessing(false);
      setLoading(false);
    }
  }

  useEffect(() => {
    // Check if we have the required OAuth parameters
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    
    if (code && state) {
      exchangeCode();
    } else {
      setLoading(false);
      notifyError('Invalid Request');
      navigate('/');
    }
  }, [location.search]); // eslint-disable-next-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D1B2A]">
      <div className="bg-[#0B1723] text-white p-8 rounded-lg w-full max-w-md flex flex-col items-center">
        <img src={loadingGif} alt="loading" className="h-12 w-12 mb-4" />
        <p>Signing you in with Google...</p>
      </div>
    </div>
  );
};

export default CallbackPage; 